import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

/**
 * Environnement navigateur « classique » : lien download sur blob (jsPDF `.save()` équivalent).
 * Sur WebView Capacitor (APK / iOS), ce flux est souvent bloqué : utiliser Filesystem + Share.
 */
export function triggerPdfBlobAnchorDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2500);
}

function blobToBase64Data(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = (): void => {
      const raw = reader.result;
      if (typeof raw !== "string") {
        reject(new Error("FileReader résultat inattendu"));
        return;
      }
      const comma = raw.indexOf(",");
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error("FileReader erreur"));
    reader.readAsDataURL(blob);
  });
}

/** Nom de fichier sûr pour le cache filesystem (pas de slash, longueur raisonnable). */
export function sanitizePdfFileNameForNative(name: string): string {
  const trimmed = name.replace(/[/\\?%*:|"<>]/g, "-").replace(/\s+/g, "_").trim() || "recap.pdf";
  const withPdf = /\.pdf$/i.test(trimmed) ? trimmed : `${trimmed}.pdf`;
  return withPdf.length > 120 ? `${withPdf.slice(0, 110)}.pdf` : withPdf;
}

/**
 * APK / app native : écrit dans le répertoire cache puis ouvre la feuille de partage
 * (« Enregistrer dans », Drive, fichiers…) — comportement habituel hors navigateur desktop.
 */
export async function savePdfBlobCapacitorNative(blob: Blob, fileName: string): Promise<void> {
  const path = sanitizePdfFileNameForNative(fileName);
  const base64 = await blobToBase64Data(blob);
  await Filesystem.writeFile({
    path,
    directory: Directory.Cache,
    data: base64,
  });
  const { uri } = await Filesystem.getUri({
    directory: Directory.Cache,
    path,
  });
  await Share.share({
    dialogTitle: path,
    title: path,
    url: uri,
  });
}

/**
 * Web → anchor download ; natif Capacitor → cache + Share ; secours anchor si erreur native.
 */
export async function deliverPdfBlobToUser(blob: Blob, fileName: string): Promise<void> {
  if (Capacitor.getPlatform() === "web") {
    triggerPdfBlobAnchorDownload(blob, sanitizePdfFileNameForNative(fileName));
    return;
  }
  try {
    await savePdfBlobCapacitorNative(blob, fileName);
  } catch (e) {
    console.warn("[mobile-pdf] Export natif impossible, fallback navigateur:", e);
    triggerPdfBlobAnchorDownload(blob, sanitizePdfFileNameForNative(fileName));
  }
}
